import Papa from 'papaparse'

import {
  CanonicalOrder,
  SchemaField,
  SourceBinding,
  SourceBindings,
  SourceColumn,
  UploadFormat,
  UploadIssue,
  ValidationResult,
} from './types'

export const normalizeHeader = (header: string) => header.trim().toLowerCase().replace(/\s+/g, ' ')

export const inspectCsv = async (file: File) => {
  const parsed = Papa.parse<string[]>(await file.text(), { skipEmptyLines: 'greedy' })
  if (parsed.errors.length) throw new Error(`The CSV could not be parsed: ${parsed.errors[0].message}`)
  const rows = parsed.data
  if (!rows.length || !rows[0].some((header) => header.trim())) throw new Error('The CSV must contain a header row.')

  const occurrences = new Map<string, number>()
  const columns = rows[0].map<SourceColumn>((header, index) => {
    const normalizedHeader = normalizeHeader(header)
    const occurrence = (occurrences.get(normalizedHeader) || 0) + 1
    occurrences.set(normalizedHeader, occurrence)
    const samples = rows
      .slice(1)
      .map((row) => row[index]?.trim())
      .filter((value): value is string => Boolean(value))
      .slice(0, 3)

    return {
      id: `${normalizedHeader}:${occurrence}`,
      index,
      header,
      normalized_header: normalizedHeader,
      occurrence,
      samples,
    }
  })

  return { columns, dataRowCount: Math.max(0, rows.length - 1) }
}

export const inferUploadFormat = (columns: SourceColumn[]): UploadFormat => {
  const headers = new Set(columns.map((column) => column.normalized_header))
  const hasOriginator = ['originator id', 'order #', 'order number'].some((header) => headers.has(header))
  const hasItemSlot = Array.from({ length: 25 }, (_, index) => index + 1).some(
    (slot) =>
      (headers.has(`item${slot}`) || headers.has(`item ${slot}`)) &&
      (headers.has(`qty${slot}`) || headers.has(`qty ${slot}`) || headers.has(`quantity${slot}`))
  )
  return hasOriginator && hasItemSlot ? 'standard' : 'configurable'
}

export const bindingForColumn = (column: SourceColumn): SourceBinding => ({
  normalized_header: column.normalized_header,
  occurrence: column.occurrence,
  position: column.index,
})

export const bindingsFromSelection = (
  fields: SchemaField[],
  columns: SourceColumn[],
  selected: Record<string, number[]>
): SourceBindings =>
  fields.reduce<SourceBindings>((bindings, field) => {
    const sources = (selected[field.key] || [])
      .map((index) => columns.find((column) => column.index === index))
      .filter((column): column is SourceColumn => Boolean(column))
      .map(bindingForColumn)
    if (sources.length) bindings[field.key] = sources
    return bindings
  }, {})

export const selectionFromBindings = (
  bindings: SourceBindings = {},
  columns: SourceColumn[] = []
): Record<string, number[]> =>
  Object.fromEntries(
    Object.entries(bindings).flatMap(([field, sources]) => {
      const indices = sources.flatMap((source) => {
        const match = columns.find(
          (column) => column.normalized_header === source.normalized_header && column.occurrence === source.occurrence
        )
        return match ? [match.index] : []
      })
      return indices.length ? [[field, indices]] : []
    })
  )

export const suggestedSelection = (fields: SchemaField[], columns: SourceColumn[]): Record<string, number[]> =>
  fields.reduce<Record<string, number[]>>((selection, field) => {
    const aliases = new Set(
      [field.key.replaceAll('_', ' '), field.label, ...(field.aliases || [])].map(normalizeHeader)
    )
    const matches = columns.filter((column) => aliases.has(column.normalized_header))
    if (matches.length === 1) selection[field.key] = [matches[0].index]
    return selection
  }, {})

export const mergeMappingSelections = (suggestions: Record<string, number[]>, saved: Record<string, number[]>) => ({
  ...suggestions,
  ...saved,
})

export const parsePositiveInteger = (value: string) => {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

export const orderLabel = (order: CanonicalOrder) => order.originator_id || 'Unidentified order'

export const orderHasIssues = (order: CanonicalOrder) =>
  order.issues.some((issue) => issue.level === 'error' || issue.level === 'warning')

export const canSubmit = (result: ValidationResult | null, dirty: boolean) =>
  Boolean(
    result &&
    result.ok &&
    !dirty &&
    result.summary.blocking_errors === 0 &&
    result.orders.some((order) => !order.excluded) &&
    !result.orders.some((order) => !order.excluded && order.issues.some((issue) => issue.level === 'error'))
  )

export const displayValue = (value: unknown, choices?: Array<{ value: string; label: string }>) => {
  const choice = choices?.find((option) => option.value === value)
  if (choice) return choice.label
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return value === null || value === undefined || value === '' ? 'None' : String(value)
}

export const issuesForOrder = (issues: UploadIssue[], orderId: string) =>
  issues.filter((issue) => issue.order_id === orderId)
